import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@/components/common/Toast';
import { sellerService } from '@/services/seller';
import { useSellerStore } from '@/store/seller';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';

export function DesktopOrderRejectReasonPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const { updateOrderStatus } = useSellerStore();

  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      showToast('거부 사유를 입력해주세요');
      return;
    }

    if (!orderId) {
      showToast('주문 정보를 찾을 수 없습니다');
      return;
    }

    setLoading(true);
    try {
      const result = await sellerService.rejectOrder(orderId, reason.trim());

      if (result.success) {
        // 스토어 상태 업데이트
        updateOrderStatus(orderId, 'CANCELLED');
        
        showToast('주문이 거부되었습니다');
        navigate('/seller/home');
      } else {
        showToast(result.error || '주문 거부에 실패했습니다');
      }
    } catch (error) {
      console.error('Order reject error:', error);
      showToast('주문 거부 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/seller/orders/${orderId}`);
  };

  return (
    <DesktopSellerPageContainer>
      <div className="max-w-[600px] mx-auto">
        {/* 헤더 */}
        <div className="flex items-center mb-8">
          <button
            onClick={handleCancel}
            className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-[28px] font-bold text-gray-800">주문 거부</h1>
        </div>

        {/* 안내 메시지 */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-8">
          <p className="text-orange-800 text-lg leading-relaxed">
            주문을 거부하는 이유를 고객에게 설명해주세요. 
            명확한 사유는 고객의 이해와 만족도 향상에 도움이 됩니다.
          </p>
        </div>

        {/* 사유 입력 */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <label className="block text-lg font-semibold text-gray-700 mb-4">
            거부 사유 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="거부 사유를 입력해주세요"
            className="w-full h-40 px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            maxLength={500}
          />
          <div className="text-right text-gray-500 mt-2">
            {reason.length}/500
          </div>
        </div>

        {/* 액션 버튼 */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={handleCancel}
            className="py-4 px-6 bg-gray-500 text-white rounded-lg font-medium text-lg hover:bg-gray-600 transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={!reason.trim() || loading}
            className={`py-4 px-6 rounded-lg font-medium text-lg transition-colors ${
              reason.trim() && !loading
                ? 'bg-orange-500 text-white hover:bg-orange-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {loading ? '처리 중...' : '주문 거부'}
          </button>
        </div>
      </div>

      <ToastContainer />
    </DesktopSellerPageContainer>
  );
}